import * as React from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';
import { compose, withProps } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import type { EnrichedEntry as EnrichedCatalogResultEntry } from 'bundles/browse/components/CatalogResultUtils';
import BrowseCarousel from 'bundles/browse/components/non-cds/BrowseCarousel';
import { translateBrowseCarouselContentForSaveLookup } from 'bundles/browse/components/utils/BookmarkUtils';
// Save/Unsave Experiment
import withSavedDataFromProducts from 'bundles/browse/components/withSavedDataFromProducts';
import { responsiveProperty } from 'bundles/browse/constants';
import type { DegreeListsEntry } from 'bundles/browse/types/degree-list';
import type {
  PreformattedDataFromBrowseForSavedDataLookup,
  PropsForSavedDataLookup,
  withSavedProductsProps,
} from 'bundles/browse/types/withSavedDataFromProducts';
import { isProductS12n } from 'bundles/browse/utils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import ImportedCollectionPlaceholder from 'bundles/payments-common/components/CollectionPlaceholder';

import _t from 'i18n!nls/browse';

import 'css!./__styles__/Collection';

type product = EnrichedCatalogResultEntry | DegreeListsEntry;

export type InputProps = {
  tag?: string;
  tagClassNames?: string;
  dotsClassName?: string;
  prevArrowClassName?: string;
  nextArrowClassName?: string;
  collectionWrapperClassName?: string;
  CollectionItem: React.ElementType;
  collection: product[];
  collectionName?: string | null;
  collectionHeader?: React.ReactNode;
  collectionFooter?: React.ReactNode;
  hideCollectionNameHeader?: boolean;
  collectionLink?: {
    url: string;
    label: string;
  } | null;
  collectionItemHeight?: number;
  loadOnClient?: boolean;
  trackingData?: Record<string, unknown>;
  forceOpenInNewTab?: boolean;
  showFreeBadge?: boolean;
  queryParameters?: { [index: string]: string };
  'data-e2e'?: string;
};

type Props = InputProps & PropsForSavedDataLookup & withSavedProductsProps;

function isDegreeEntry(product: product): product is DegreeListsEntry {
  return (product as DegreeListsEntry).degree !== undefined;
}

/** @deprecated replaced by the new ProductCardCollection, do not use this component at all cost */
export class Collection extends React.PureComponent<Props> {
  static contextTypes = {
    _eventData: PropTypes.object,
  };

  declare context: LegacyContextType<typeof Collection.contextTypes>;

  getDataToTrack = ({
    product = {} as product,
    trackingData,
    entityIndex,
  }: {
    product: product;
    trackingData: Record<string, unknown>;
    entityIndex?: number;
  }) => {
    const isS12n = product && isProductS12n(product);
    let entityType = isS12n ? 's12n' : 'course';
    let productId: string | undefined;
    let name: string | undefined;
    if (isDegreeEntry(product)) {
      entityType = 'degree';
      productId = product.degree?.id;
      name = product.degree?.name;
    } else {
      productId = product?.id;
      name = product?.name;
    }
    const entityId = `${entityType}~${productId}`;
    return { entityId, entityIndex, name, ...trackingData };
  };

  renderCollectionCard = (productObj: product, index: number) => {
    const {
      CollectionItem,
      tag,
      tagClassNames,
      trackingData,
      forceOpenInNewTab,
      showFreeBadge,
      queryParameters,
      savedProductData,
      loadingSavedProducts,
    } = this.props;
    // @ts-expect-error Temporary ignore while generated types are investigated (looks like these are avail but ts complains)
    const { id } = productObj;

    // @ts-expect-error Find Does Not Exist On Array
    const isSaved = !!savedProductData?.find((savedProductItem) => savedProductItem.productId === id);

    return (
      <CollectionItem
        tag={tag}
        tagClassNames={tagClassNames}
        product={productObj}
        entityIndex={index}
        trackingData={trackingData && this.getDataToTrack({ product: productObj, trackingData, entityIndex: index })}
        forceOpenInNewTab={forceOpenInNewTab}
        showFreeBadge={showFreeBadge}
        saved={isSaved}
        queryParameters={queryParameters}
        loadingSavedProducts={loadingSavedProducts}
        {...productObj}
      />
    );
  };

  renderCollectionName = () => {
    const { collectionName, hideCollectionNameHeader, collectionLink } = this.props;

    if (hideCollectionNameHeader) {
      return null;
    }

    const collectionNameWithOrWithoutLink =
      !user.isAuthenticatedUser() && collectionLink?.url ? (
        <TrackedA
          className="collection-link"
          href={collectionLink.url}
          data={{ url: collectionLink.url }}
          trackingName="collection_carousel_title_link_to_collection_page"
        >
          {collectionName || ''}
        </TrackedA>
      ) : (
        collectionName || ''
      );
    return (
      <div className="horizontal-box align-items-spacebetween">
        <FormattedMessage
          message={_t('{title}')}
          title={<h2 className="collection-name"> {collectionNameWithOrWithoutLink} </h2>}
        />
      </div>
    );
  };

  render() {
    const {
      collectionHeader,
      collectionFooter,
      collectionName,
      collectionWrapperClassName,
      prevArrowClassName,
      nextArrowClassName,
      dotsClassName,
      'data-e2e': e2eTesting,
      trackingData,
      collection,
      loadOnClient,
      collectionItemHeight,
    } = this.props;
    const style = collectionItemHeight ? { height: collectionItemHeight } : {};

    const e2eTestingObj: { [key: string]: string } = {};
    if (e2eTesting) e2eTestingObj['data-e2e'] = e2eTesting;

    return (
      <section
        className="rc-Collection"
        {...e2eTestingObj}
        aria-label={_t('#{collectionName} Carousel', { collectionName })}
      >
        {this.renderCollectionName()}
        {collectionHeader}
        <div className={cx('rc-desktopCollection', collectionWrapperClassName)}>
          <BrowseCarousel
            loadOnlyOnClient={loadOnClient}
            style={style}
            responsive={responsiveProperty}
            prevArrowClassName={prevArrowClassName}
            nextArrowClassName={nextArrowClassName}
            dotsClassName={dotsClassName}
            slidesToShow={collection.length > 4 ? 4 : collection.length}
            slidesToScroll={collection.length > 4 ? 4 : collection.length}
            trackingData={trackingData}
            enableRtl
          >
            {collection.map((productObj, index) => (
              <div
                className="rc-CollectionItem-wrapper"
                key={isDegreeEntry(productObj) ? productObj.degree?.slug : productObj?.id}
              >
                {this.renderCollectionCard(productObj, index)}
              </div>
            ))}
          </BrowseCarousel>
        </div>
        {collectionFooter}
      </section>
    );
  }
}

/** @deprecated replaced by the new ProductCardCollection, do not use this component at all cost */
export default compose<Props, InputProps>()(Collection);

/** @deprecated replaced by the new ProductCardCollection, do not use this component at all cost */
export const CollectionWithSaveExperiment = compose<Props, InputProps>(
  withProps<PropsForSavedDataLookup, InputProps>(({ collection }) => {
    const products = (collection as PreformattedDataFromBrowseForSavedDataLookup[]).map(
      translateBrowseCarouselContentForSaveLookup
    );

    return {
      products,
    };
  }),
  withSavedDataFromProducts
)(Collection);

/** @deprecated replaced by the new ProductCardCollection, do not use this component at all cost */
export const CollectionPlaceholder = ImportedCollectionPlaceholder;
